// src/components/layout/MapPageHeader.jsx
import React from "react";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Select,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../../../i18n/I18nContext";
import { useAuth } from "../../utils/AuthContext";
import { hasRole } from "../../utils/auth";
import LanguageSwitcher from "../../common/LanguageSwitcher";

export const HEADER_HEIGHT = 58;

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function buildAssetUrl(path) {
    if (!path) return "/images/hero_map.jpg";
    if (/^https?:\/\//i.test(path)) return path;
    return `${API_BASE}${path}`;
}

function parsePopularSearches(value) {
    return String(value || "")
        .split(/[\n,;]/)
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, 8);
}

export default function MapPageHeader({ domainConfig = {}, showHero = true }) {
    const { t } = useI18n();
    const { user, isAuthenticated } = useAuth();
    const isAdmin = isAuthenticated && hasRole(user, "admin");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const navigate = useNavigate();
    const location = useLocation();

    const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
    const [query, setQuery] = React.useState(searchParams.get("q") || "");
    const [country, setCountry] = React.useState(searchParams.get("country") || "all");
    const [countries, setCountries] = React.useState([]);
    const [anchorEl, setAnchorEl] = React.useState(null);

    React.useEffect(() => {
        setQuery(searchParams.get("q") || "");
        setCountry(searchParams.get("country") || "all");
    }, [searchParams]);

    React.useEffect(() => {
        let active = true;

        Promise.all([
            fetch(`${API_BASE}/api/filteredcompanies?category_ids=1`, { credentials: "include" }),
            fetch(`${API_BASE}/api/filteredcompanies?category_ids=2`, { credentials: "include" }),
        ])
            .then(async ([hotelsRes, restaurantsRes]) => {
                const hotels = hotelsRes.ok ? await hotelsRes.json() : [];
                const restaurants = restaurantsRes.ok ? await restaurantsRes.json() : [];
                const nextCountries = Array.from(
                    new Set(
                        [...(Array.isArray(hotels) ? hotels : []), ...(Array.isArray(restaurants) ? restaurants : [])]
                            .map((item) => item?.country)
                            .filter(Boolean)
                    )
                ).sort((a, b) => a.localeCompare(b, "de", { sensitivity: "base" }));

                if (active) setCountries(nextCountries);
            })
            .catch(() => {
                if (active) setCountries([]);
            });

        return () => {
            active = false;
        };
    }, []);

    const navItems = [
        { key: "hotels", label: t("nav.hotels"), url: "/hotels" },
        { key: "restaurants", label: t("nav.restaurants"), url: "/restaurants" },
        { key: "latestMembers", label: t("nav.latestMembers"), url: "/neueste-mitglieder" },
        { key: "offers", label: t("nav.offers"), url: "/" },
        { key: "about", label: t("nav.about"), url: "/impressum" },
        { key: "contact", label: t("nav.contact"), url: "/kontakt" },
        ...(isAdmin ? [{ key: "internal", label: t("nav.internal"), url: "/admin" }] : []),
    ];

    const heroTitle = domainConfig.header_title || t("map.header.title");
    const heroSubline = domainConfig.header_subline || t("map.header.subtitle");
    const popularSearches = parsePopularSearches(domainConfig.popular_searches);
    const heroImage = buildAssetUrl(domainConfig.page_header_image);
    const countryOptions = country !== "all" && !countries.includes(country)
        ? [country, ...countries]
        : countries;

    const submitSearch = (nextQuery = query) => {
        const params = new URLSearchParams();
        const cleanQuery = (nextQuery || "").trim();
        if (cleanQuery) params.set("q", cleanQuery);
        if (country !== "all") params.set("country", country);
        navigate({ pathname: "/", search: params.toString() ? `?${params.toString()}` : "" });
    };

    const handleNavClick = (url) => {
        navigate(url);
        setAnchorEl(null);
    };

    return (
        <Box component="header" sx={{ bgcolor: "background.paper", pt: `${HEADER_HEIGHT}px` }}>
            <Box
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: (theme) => theme.zIndex.appBar + 2,
                    borderBottom: "1px solid #e5e7eb",
                    bgcolor: "background.paper",
                    boxShadow: "0 2px 12px rgba(15,23,42,0.08)",
                }}
            >
                <Box
                    sx={{
                        maxWidth: 1540,
                        mx: "auto",
                        px: { xs: 2, md: 4 },
                        height: HEADER_HEIGHT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Box
                        component="img"
                        src={domainConfig.header_logo ? buildAssetUrl(domainConfig.header_logo) : "/images/HRI_Logo_rz_RGB.png"}
                        alt={domainConfig.page_title || "Logo"}
                        onClick={() => navigate("/")}
                        sx={{
                            height: { xs: 34, md: 44 },
                            width: "auto",
                            maxWidth: { xs: 150, md: 200 },
                            objectFit: "contain",
                            cursor: "pointer",
                            display: "block",
                        }}
                    />

                    {!isMobile && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: { md: 1.5, lg: 3.5 } }}>
                            {navItems.map((item) => (
                                <Button
                                    key={item.key}
                                    variant="text"
                                    onClick={() => handleNavClick(item.url)}
                                    sx={{
                                        color: "var(--domain-text)",
                                        fontSize: "0.9rem",
                                        fontWeight: 700,
                                        textTransform: "none",
                                        p: 0,
                                        minWidth: "auto",
                                        "&:hover": { bgcolor: "transparent", color: "var(--domain-primary)" },
                                    }}
                                >
                                    {item.label}
                                </Button>
                            ))}
                        </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {isMobile && (
                            <>
                                <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
                                    <MenuIcon />
                                </IconButton>
                                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                                    {navItems.map((item) => (
                                        <MenuItem key={item.key} onClick={() => handleNavClick(item.url)}>
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}
                        <LanguageSwitcher />
                    </Box>
                </Box>
            </Box>

            {showHero && (
                <Box
                    sx={{
                        minHeight: { xs: 430, md: 475 },
                        display: "flex",
                        alignItems: "center",
                        color: "#fff",
                        backgroundImage: `linear-gradient(90deg, rgba(15,23,42,0.70) 0%, rgba(15,23,42,0.45) 42%, rgba(15,23,42,0.08) 100%), url("${heroImage}")`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 1540,
                            mx: "auto",
                            px: { xs: 2, md: 4 },
                            pt: { xs: 5, md: 7 },
                            pb: { xs: 5, md: 6 },
                        }}
                    >
                        <Box sx={{ maxWidth: 850 }}>
                            <Box
                                component="h1"
                                dangerouslySetInnerHTML={{ __html: heroTitle }}
                                sx={{
                                    fontWeight: 650,
                                    fontSize: { xs: "1.9rem", md: "2.85rem" },
                                    lineHeight: 1.18,
                                    letterSpacing: 0,
                                    textShadow: "0 3px 16px rgba(0,0,0,0.35)",
                                    mb: 2,
                                    maxWidth: 720,
                                    "& *": {
                                        font: "inherit",
                                        lineHeight: "inherit",
                                        letterSpacing: 0,
                                        color: "inherit",
                                    },
                                    "& p, & h1, & h2, & h3, & h4, & h5, & h6": {
                                        m: 0,
                                    },
                                    "& strong, & b": {
                                        fontWeight: 700,
                                    },
                                    "& em, & i": {
                                        fontStyle: "italic",
                                    },
                                }}
                            />
                            <Box
                                component="div"
                                dangerouslySetInnerHTML={{ __html: heroSubline }}
                                sx={{
                                    fontSize: { xs: "0.94rem", md: "1.02rem" },
                                    fontWeight: 500,
                                    lineHeight: 1.45,
                                    letterSpacing: 0,
                                    textShadow: "0 2px 10px rgba(0,0,0,0.38)",
                                    mb: 3,
                                    maxWidth: 680,
                                    "& *": {
                                        font: "inherit",
                                        lineHeight: "inherit",
                                        letterSpacing: 0,
                                        color: "inherit",
                                    },
                                    "& p, & h1, & h2, & h3, & h4, & h5, & h6": {
                                        m: 0,
                                    },
                                    "& strong, & b": {
                                        fontWeight: 650,
                                    },
                                    "& a": {
                                        color: "#fff",
                                        textDecorationColor: "rgba(255,255,255,0.6)",
                                    },
                                }}
                            />

                            <Box
                                component="form"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    submitSearch();
                                }}
                                sx={{
                                    width: "100%",
                                    maxWidth: 840,
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 230px 150px" },
                                    gap: 0,
                                    bgcolor: "background.paper",
                                    borderRadius: 1,
                                    p: 1,
                                    boxShadow: "0 10px 30px rgba(15,23,42,0.30)",
                                }}
                            >
                                <TextField
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder={t("map.search.queryPlaceholder")}
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 1,
                                            "& fieldset": { borderColor: "transparent" },
                                        },
                                    }}
                                />
                                <Select
                                    value={country}
                                    onChange={(event) => setCountry(event.target.value)}
                                    size="small"
                                    displayEmpty
                                    sx={{
                                        mx: { xs: 0, sm: 1 },
                                        borderRadius: 1,
                                        "& fieldset": { borderColor: "transparent" },
                                    }}
                                >
                                    <MenuItem value="all">{t("map.search.countryAll")}</MenuItem>
                                    {countryOptions.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disableElevation
                                    endIcon={<SearchIcon fontSize="small" />}
                                    sx={{
                                        bgcolor: "var(--domain-primary)",
                                        color: "var(--domain-primary-contrast)",
                                        borderRadius: 1,
                                        fontWeight: 900,
                                        textTransform: "none",
                                        "&:hover": { bgcolor: "var(--domain-primary-dark)" },
                                    }}
                                >
                                    {t("map.search.searchButton")}
                                </Button>
                            </Box>

                            {popularSearches.length > 0 && (
                                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <Typography sx={{ fontSize: "0.86rem", fontWeight: 800 }}>
                                        {t("home.popularSearches")}:
                                    </Typography>
                                    {popularSearches.map((item) => (
                                        <Button
                                            key={item}
                                            size="small"
                                            onClick={() => submitSearch(item)}
                                            sx={{
                                                minHeight: 26,
                                                px: 1.25,
                                                py: 0.15,
                                                color: "#fff",
                                                border: "1px solid rgba(255,255,255,0.48)",
                                                bgcolor: "rgba(15,23,42,0.34)",
                                                borderRadius: 1,
                                                fontSize: "0.76rem",
                                                fontWeight: 700,
                                                textTransform: "none",
                                                backdropFilter: "blur(8px)",
                                                "&:hover": { bgcolor: "rgba(255,138,24,0.82)" },
                                            }}
                                        >
                                            {item}
                                        </Button>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
